import moment from "moment"

type LocaleLoader = () => Promise<unknown>

/**
 * Explicit loaders so bundlers emit one chunk per locale.
 * A dynamic `import(\`moment/locale/${code}\`)` would pull every locale into the bundle.
 *
 * Keep in sync with the languages we support for Moment formatting / relative time.
 */
const MOMENT_LOCALE_LOADERS: Record<string, LocaleLoader> = {
  ar: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-ar" */ "moment/locale/ar"),
  cs: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-cs" */ "moment/locale/cs"),
  de: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-de" */ "moment/locale/de"),
  es: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-es" */ "moment/locale/es"),
  fr: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-fr" */ "moment/locale/fr"),
  hi: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-hi" */ "moment/locale/hi"),
  hu: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-hu" */ "moment/locale/hu"),
  lv: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-lv" */ "moment/locale/lv"),
  pl: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-pl" */ "moment/locale/pl"),
  pt: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-pt" */ "moment/locale/pt"),
  ro: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-ro" */ "moment/locale/ro"),
  sk: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-sk" */ "moment/locale/sk"),
  sl: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-sl" */ "moment/locale/sl"),
  sv: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-sv" */ "moment/locale/sv"),
  ta: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-ta" */ "moment/locale/ta"),
  th: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-th" */ "moment/locale/th"),
  tr: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-tr" */ "moment/locale/tr"),
  uk: () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-uk" */ "moment/locale/uk"),
  "zh-cn": () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-zh-cn" */ "moment/locale/zh-cn"),
  "zh-hk": () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-zh-hk" */ "moment/locale/zh-hk"),
  "zh-mo": () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-zh-mo" */ "moment/locale/zh-mo"),
  "zh-tw": () =>
    // @ts-expect-error
    import(/* webpackChunkName: "moment-locale-zh-tw" */ "moment/locale/zh-tw")
}

function normalizeLanguage(language: string): string {
  return language.toLowerCase().replace(/_/g, "-")
}

/**
 * Load Moment locale data for `language` (if needed) and activate it.
 * English is built into Moment and needs no extra import.
 */
export async function setMomentLocale(
  language?: string | null
): Promise<string> {
  const normalized = normalizeLanguage(language || "en")
  const candidates = [normalized]
  const primary = normalized.split("-")[0]
  if (primary && primary !== normalized) {
    candidates.push(primary)
  }

  for (const candidate of candidates) {
    if (candidate === "en") {
      moment.locale("en")
      return "en"
    }

    const loader = MOMENT_LOCALE_LOADERS[candidate]
    if (loader) {
      await loader()
      moment.locale(candidate)
      return candidate
    }
  }

  moment.locale("en")
  return "en"
}
